import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Dialog,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddCircleOutlineOutlined from "@mui/icons-material/AddCircleOutlineOutlined";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import SortIcon from "@mui/icons-material/Sort";
import StarIcon from "@mui/icons-material/Star";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import { Package } from "lucide-react";
import { useModalStyles } from "../../common/useModalStyles";
import { ChevronRight } from "@mui/icons-material";

interface ProductKitSubItem {
  inactiveDate?: string | null;
  productItemDescription: string;
  productItemHcpcsCode: string;
  productItemId: number;
  productItemKitItemId: number;
  productName: string;
  qty: number;
  refProductId: number;
  size: string;
}

interface ProductItem {
  id: number;
  hcpcsCode: string;
  size: string;
  description: string;
  noOfDays?: string;
  sku?: string;
  productKitSubItems?: ProductKitSubItem[];
}

interface ProductItemSelectPopupProps {
  open: boolean;
  onClose: () => void;
  productOptions: ProductItem[];
  onSelectItem?: (selectedItem: ProductItem | ProductItem[]) => void;
  multiSelect?: boolean;
}

const DURATION_PATTERNS = ["14-Day", "21-Day", "28-Day", "30-Day"];
const TYPE_PATTERNS = ["SAP Powder", "Collagen Pad", "Sacrum Powder", "Heel Powder"];

const parseDuration = (desc: string) =>
  DURATION_PATTERNS.find((d) => desc.includes(d)) || null;

const parseType = (desc: string) =>
  TYPE_PATTERNS.find((t) => desc.includes(t)) || null;


const ProductItemSelectPopup: React.FC<ProductItemSelectPopupProps> = ({
  open,
  onClose,
  productOptions,
  onSelectItem,
  multiSelect = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    isDark,
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    bodySx,
    footerSx,
    closeIconSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();

  const [selected, setSelected] = useState<ProductItem | null>(null);
  const [selectedMultiple, setSelectedMultiple] = useState<ProductItem[]>([]);

  const [searchQuery, setSearchQuery]         = useState("");
  const [activeDuration, setActiveDuration]   = useState<string | null>(null);
  const [activeSize, setActiveSize]           = useState<string | null>(null);
  const [activeType, setActiveType]           = useState<string | null>(null);
  const [expandedId, setExpandedId]           = useState<number | null>(null);
  const [viewMode,   setViewMode]             = useState<"list" | "grid">("list");

  const durations = useMemo(
    () => Array.from(new Set(productOptions.map((o) => o.noOfDays ?? null).filter(Boolean) as string[])),
    [productOptions]
  );
  const sizes = useMemo(
    () => Array.from(new Set(productOptions.map((o) => o.size ?? "").filter(Boolean))),
    [productOptions]
  );
  const types = useMemo(
    () => Array.from(new Set(productOptions.map((o) => parseType(o.description ?? "")).filter(Boolean) as string[])),
    [productOptions]
  );

  
  const filteredOptions = useMemo(() => {
    return productOptions.filter((o) => {
      if (activeDuration && o.noOfDays !== activeDuration) return false;
      if (activeSize && (o.size ?? "") !== activeSize) return false;
      if (activeType && !(o.description ?? "").includes(activeType)) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (
          !(o.description ?? "").toLowerCase().includes(q) &&
          !(o.hcpcsCode  ?? "").toLowerCase().includes(q) &&
          !(o.size       ?? "").toLowerCase().includes(q)
        ) return false;
      }
      return true;
    });
  }, [productOptions, activeDuration, activeSize, activeType, searchQuery]);

  const clearAllFilters = () => {
    setActiveDuration(null);
    setActiveSize(null);
    setActiveType(null);
    setSearchQuery("");
  };

  const hasActiveFilters = activeDuration || activeSize || activeType || searchQuery;

  const handleAdd = () => {
    if (multiSelect && selectedMultiple.length > 0) {
      onSelectItem?.(selectedMultiple);
    } else if (!multiSelect && selected) {
      onSelectItem?.(selected);
    }
  };

  const handleClose = () => {
    setSelected(null);
    setSelectedMultiple([]);
    onClose();
  };

  const handleItemClick = (option: ProductItem) => {
    if (multiSelect) {
      setSelectedMultiple((prev) => {
        const exists = prev.some((item) => item.id === option.id);
        return exists ? prev.filter((item) => item.id !== option.id) : [...prev, option];
      });
    } else {
      setSelected((prev) => (prev?.id === option.id ? null : option));
      setExpandedId((prev) => (prev === option.id ? null : option.id));
    }
  };

  const isItemSelected = (option: ProductItem) =>
    multiSelect
      ? selectedMultiple.some((item) => item.id === option.id)
      : selected?.id === option.id;

  const hasSelection = multiSelect ? selectedMultiple.length > 0 : selected !== null;
  
  const sidebarBg    = isDark ? "rgba(255,255,255,.02)" : "#F8FAFC";
  const sidebarBdr   = isDark ? "rgba(255,255,255,.06)" : "#EEF2F6";
  const filterBtnBase= {
    fontSize: "0.75rem", fontWeight: 500, borderRadius: "8px",
    px: 1.25, py: 0.6, cursor: "pointer", userSelect: "none" as const,
    border: "1px solid", transition: "all .15s",
  };
  const filterBtnOff = {
    ...filterBtnBase,
    borderColor: isDark ? "rgba(255,255,255,.10)" : "#DFE3E8",
    background:  isDark ? "rgba(255,255,255,.04)" : "#fff",
    color: tokens.textSecondary,
    "&:hover": { borderColor: tokens.accent, color: tokens.accent },
  };
  const filterBtnOn = {
    ...filterBtnBase,
    borderColor: tokens.accent,
    background: isDark ? "rgba(20,184,166,.18)" : "rgba(20,184,166,.10)",
    color: tokens.accent,
    fontWeight: 700,
  };

  const rowBg    = isDark ? "rgba(255,255,255,.025)" : "#fff";
  const rowBdr   = isDark ? "rgba(255,255,255,.06)"  : "#EEF2F6";
  const rowHover = isDark ? "rgba(255,255,255,.05)"  : "#F8FAFC";
  const rowSelBg = isDark ? "rgba(20,184,166,.10)"   : "rgba(20,184,166,.06)";
  const rowSelBdr= tokens.accent;

  const tagStyle = (color: string) => ({
    display: "inline-flex", alignItems: "center",
    px: 0.9, py: 0.2, borderRadius: "6px",
    fontSize: "0.68rem", fontWeight: 600,
    background: isDark ? `${color}18` : `${color}14`,
    color: color,
    border: `1px solid ${color}30`,
  });

  const TAG_COLORS: Record<string, string> = {
    "14-Day": "#3B82F6", "21-Day": "#8B5CF6",
    "28-Day": "#F59E0B", "30-Day": "#10B981",
    "SAP Powder": "#06B6D4", "Collagen Pad": "#8B5CF6",
    "Sacrum Powder": "#F59E0B", "Heel Powder": "#EF4444",
  };
  const sizeColor = "#6366F1";

  const getItemTags = (option: ProductItem) => {
    const tags: { label: string; color: string }[] = [];
    const dur  = parseDuration(option.description ?? "");
    const type = parseType(option.description ?? "");
    if (dur)           tags.push({ label: dur,        color: TAG_COLORS[dur]  || "#10B981" });
    if (option.size)   tags.push({ label: option.size, color: sizeColor });
    if (type)          tags.push({ label: type,       color: TAG_COLORS[type] || "#06B6D4" });
    return tags;
  };

  const FilterSection = ({
    label,
    items,
    active,
    onSelect,
  }: {
    label: string;
    items: string[];
    active: string | null;
    onSelect: (v: string | null) => void;
  }) => (
    <Box sx={{ mb: 2.5 }}>
      <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: tokens.textSecondary, mb: 1 }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        {items.map((item) => (
          <Box
            key={item}
            onClick={() => onSelect(active === item ? null : item)}
            sx={active === item ? filterBtnOn : filterBtnOff}
          >
            {item}
            {active === item && (
              <Box component="span" sx={{ ml: 0.5, fontSize: "0.6rem" }}>✓</Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      fullScreen={isMobile}
      PaperProps={{
        elevation: 0,
        sx: {
          ...paperSx(isMobile),
          height: isMobile ? "100%" : "80vh",
          maxHeight: isMobile ? "100%" : "80vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
      aria-labelledby="product-item-select-dialog-title"
    >
      <Box sx={{
        ...headerSx,
        flexShrink: 0,
      }}>
        <Box sx={iconBoxSx(tokens.accent)}>
          <Package size={20} color={tokens.accent} strokeWidth={1.8} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            id="product-item-select-dialog-title"
            sx={{ fontSize: "0.95rem", fontWeight: 700, color: tokens.textPrimary, lineHeight: 1.2 }}
          >
            Select {multiSelect ? "Products" : "KIT"}
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: tokens.textSecondary, mt: 0.25 }}>
            {multiSelect ? "Choose one or more items" : "Choose a product kit"}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={closeIconSx}>
          <CloseIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Box>

      <Box sx={{ display: "flex", flex: 1, overflow: "hidden", minHeight: 0 }}>
        {!isMobile && (
          <Box sx={{
            width: 220, flexShrink: 0,
            borderRight: `1px solid ${sidebarBdr}`,
            background: sidebarBg,
            overflowY: "auto",
            px: 2, py: 2,
            "&::-webkit-scrollbar": { width: 3 },
            "&::-webkit-scrollbar-thumb": { background: isDark ? "rgba(255,255,255,.08)" : "#CBD5E1", borderRadius: 3 },
          }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <FilterListIcon sx={{ fontSize: 15, color: tokens.textSecondary }} />
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: tokens.textPrimary }}>
                  Filters
                </Typography>
              </Box>
              {hasActiveFilters && (
                <Box
                  onClick={clearAllFilters}
                  sx={{ fontSize: "0.72rem", color: tokens.accent, cursor: "pointer", fontWeight: 600, "&:hover": { opacity: 0.8 } }}
                >
                  Clear all
                </Box>
              )}
            </Box>

            {durations.length > 0 && (
              <FilterSection label="Duration" items={durations} active={activeDuration} onSelect={setActiveDuration} />
            )}
            {sizes.length > 0 && (
              <FilterSection label="Wound Size" items={sizes} active={activeSize} onSelect={setActiveSize} />
            )}
            {types.length > 0 && (
              <FilterSection label="Type" items={types} active={activeType} onSelect={setActiveType} />
            )}

            <Box sx={{
              mt: 2, p: 1.25, borderRadius: "9px",
              background: isDark ? "rgba(20,184,166,.07)" : "rgba(20,184,166,.06)",
              border: `1px solid ${isDark ? "rgba(20,184,166,.18)" : "#99F6E4"}`,
            }}>
              <Typography sx={{ fontSize: "0.7rem", color: isDark ? "#5EEAD4" : "#0F766E", lineHeight: 1.6 }}>
                Use filters or search to quickly find the right kit.
              </Typography>
            </Box>
          </Box>
        )}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, overflow: "hidden" }}>
          <Box sx={{
            px: 2.5, py: 1.5,
            borderBottom: `1px solid ${isDark ? "rgba(255,255,255,.05)" : "#F1F5F9"}`,
            display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0,
          }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by size, type, or kit name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 17, color: tokens.textSecondary }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "9px",
                  background: isDark ? "rgba(255,255,255,.04)" : "#F8FAFC",
                  fontSize: "0.82rem",
                  "& fieldset": { borderColor: isDark ? "rgba(255,255,255,.10)" : "#DFE3E8" },
                  "&:hover fieldset": { borderColor: tokens.accent },
                  "&.Mui-focused fieldset": { borderColor: tokens.accent },
                },
                "& .MuiInputBase-input": { color: tokens.textPrimary },
              }}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0, fontSize: "0.75rem", color: tokens.textSecondary, whiteSpace: "nowrap" }}>
              <SortIcon sx={{ fontSize: 15 }} />
              Sort by: Relevance
            </Box>
            <Box sx={{
              display: "flex", alignItems: "center",
              border: `1px solid ${isDark ? "rgba(255,255,255,.10)" : "#DFE3E8"}`,
              borderRadius: "9px", overflow: "hidden", flexShrink: 0,
            }}>
              {(["list", "grid"] as const).map((mode) => (
                <Box
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  sx={{
                    width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: "pointer", transition: "all .15s",
                    background: viewMode === mode
                      ? (isDark ? tokens.accent : tokens.accent)
                      : "transparent",
                    "&:hover": viewMode !== mode ? { background: isDark ? "rgba(255,255,255,.06)" : "#F1F5F9" } : {},
                  }}
                >
                  {mode === "grid"
                    ? <GridViewIcon sx={{ fontSize: 16, color: viewMode === mode ? "#fff" : tokens.textSecondary }} />
                    : <ViewListIcon sx={{ fontSize: 16, color: viewMode === mode ? "#fff" : tokens.textSecondary }} />
                  }
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ px: 2.5, pt: 1.25, pb: 0.5, flexShrink: 0 }}>
            <Typography sx={{ fontSize: "0.75rem", color: tokens.textSecondary }}>
              {filteredOptions.length} result{filteredOptions.length !== 1 ? "s" : ""} found
            </Typography>
          </Box>

          <Box sx={{
            flex: 1, overflowY: "auto", px: 2.5, pb: 2,
            "&::-webkit-scrollbar": { width: 4 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": { background: isDark ? "rgba(255,255,255,.10)" : "#CBD5E1", borderRadius: 4 },
          }}>
            {filteredOptions.length === 0 ? (
              <Box sx={{ py: 6, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                <SearchIcon sx={{ fontSize: 36, color: isDark ? "rgba(255,255,255,.10)" : "#D1D5DB" }} />
                <Typography sx={{ fontSize: "0.85rem", color: tokens.textSecondary, fontWeight: 600 }}>No kits found</Typography>
                <Typography sx={{ fontSize: "0.75rem", color: tokens.textSecondary, opacity: 0.7 }}>Try adjusting your search or filters</Typography>
              </Box>
            ) : (
              <Box sx={viewMode === "grid" ? {
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: 1.25,
              } : {}}>
              {filteredOptions.map((option) => {
                const isSelected = isItemSelected(option);
                const tags = getItemTags(option);
                const isExpanded = expandedId === option.id;
                const hasSubItems = Array.isArray(option.productKitSubItems) && option.productKitSubItems.length > 0;
                const SubItems = () => (
                  <Collapse in={isSelected && hasSubItems} timeout="auto" unmountOnExit>
                    <Box sx={{
                      mt: 0.75, px: 1.25, py: 1,
                      borderRadius: "9px",
                      background: isDark ? "rgba(255,255,255,.02)" : "#F8FAFC",
                      border: `1px solid ${isDark ? "rgba(255,255,255,.06)" : "#EEF2F6"}`,
                    }}>
                      {option.productKitSubItems!.map((subItem, idx) => (
                        <Box key={idx} sx={{
                          display: "flex", alignItems: "center", gap: 1,
                          py: 0.75, px: 1, mb: 0.5,
                          borderRadius: "7px",
                          background: isDark ? "rgba(255,255,255,.04)" : "#fff",
                          border: `1px solid ${isDark ? "rgba(255,255,255,.06)" : "#E2E8F0"}`,
                          fontSize: "0.72rem", color: tokens.textSecondary,
                        }}>
                          <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: tokens.accent, flexShrink: 0 }} />
                          <Box sx={{ fontWeight: 600, color: tokens.textPrimary }}>{subItem.productItemHcpcsCode}</Box>
                          {subItem.size && <Box>– {subItem.size}</Box>}
                          <Box>– {subItem.productItemDescription}</Box>
                          {subItem.qty > 1 && (
                            <Box sx={{ ml: "auto", fontWeight: 700, color: tokens.accent }}>×{subItem.qty}</Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Collapse>
                );

                if (viewMode === "grid") {
                  return (
                    <Box
                      key={option.id}
                      onClick={() => handleItemClick(option)}
                      sx={{
                        borderRadius: "12px", cursor: "pointer", p: 1.5,
                        border: `1px solid ${isSelected ? rowSelBdr : rowBdr}`,
                        background: isSelected ? rowSelBg : rowBg,
                        transition: "all .15s",
                        display: "flex", flexDirection: "column", gap: 1,
                        position: "relative",
                        alignSelf: "start",
                        "&:hover": { background: isSelected ? rowSelBg : rowHover, borderColor: isSelected ? rowSelBdr : tokens.accent + "60" },
                      }}
                    >
                      {isSelected && (
                        <Box sx={{
                          position: "absolute", top: 8, right: 8,
                          width: 20, height: 20, borderRadius: "50%",
                          background: tokens.accent, display: "flex", alignItems: "center", justifyContent: "center",
                          boxShadow: `0 0 0 3px ${isDark ? "rgba(20,184,166,.2)" : "rgba(20,184,166,.15)"}`,
                        }}>
                          <Box component="span" sx={{ color: "#fff", fontSize: "0.65rem", fontWeight: 700 }}>✓</Box>
                        </Box>
                      )}
                      <Box sx={{
                        width: 40, height: 40, borderRadius: "10px",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        background: isSelected ? (isDark ? "rgba(20,184,166,.18)" : "rgba(20,184,166,.12)") : (isDark ? "rgba(255,255,255,.06)" : "#F1F5F9"),
                        border: `1px solid ${isSelected ? tokens.accent + "40" : (isDark ? "rgba(255,255,255,.08)" : "#E2E8F0")}`,
                      }}>
                        <Package size={20} color={isSelected ? tokens.accent : tokens.textSecondary} strokeWidth={1.6} />
                      </Box>
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        {tags.slice(0, 2).map((tag, ti) => (
                          <Box key={ti} sx={{ ...tagStyle(tag.color), fontSize: "0.62rem" }}>{tag.label}</Box>
                        ))}
                      </Box>
                      <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: tokens.textPrimary, lineHeight: 1.3 }}>
                        {option.description}
                      </Typography>
                      {isSelected && hasSubItems && (
                        <Box
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            mt: 0.5, pt: 1,
                            borderTop: `1px solid ${isDark ? "rgba(255,255,255,.08)" : "rgba(20,184,166,.2)"}`,
                            display: "flex", flexDirection: "column", gap: 0.6,
                          }}
                        >
                          {option.productKitSubItems!.map((subItem, idx) => (
                            <Box key={idx} sx={{
                              display: "flex", alignItems: "flex-start", gap: 0.75,
                              py: 0.6, px: 0.75, borderRadius: "7px",
                              background: isDark ? "rgba(255,255,255,.04)" : "rgba(255,255,255,.7)",
                              border: `1px solid ${isDark ? "rgba(255,255,255,.06)" : "#E2E8F0"}`,
                            }}>
                              <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: tokens.accent, flexShrink: 0, mt: "3px" }} />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5 }}>
                                  <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: tokens.textPrimary }}>
                                    {subItem.productItemHcpcsCode}
                                  </Typography>
                                  {subItem.qty > 1 && (
                                    <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: tokens.accent, flexShrink: 0 }}>
                                      ×{subItem.qty}
                                    </Typography>
                                  )}
                                </Box>
                                <Typography sx={{ fontSize: "0.67rem", color: tokens.textSecondary, lineHeight: 1.4 }}>
                                  {subItem.size ? `${subItem.size} – ` : ""}{subItem.productItemDescription}
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  );
                }

                return (
                  <Box key={option.id} sx={{ mb: 0.75 }}>
                    <Box
                      onClick={() => handleItemClick(option)}
                      sx={{
                        display: "flex", alignItems: "center", gap: 1.5,
                        px: 1.5, py: 1.5,
                        borderRadius: isSelected && hasSubItems ? "10px 10px 0 0" : "10px",
                        cursor: "pointer",
                        border: `1px solid ${isSelected ? rowSelBdr : rowBdr}`,
                        borderBottom: isSelected && hasSubItems ? "none" : undefined,
                        background: isSelected ? rowSelBg : rowBg,
                        transition: "all .15s",
                        "&:hover": { background: isSelected ? rowSelBg : rowHover, borderColor: isSelected ? rowSelBdr : tokens.accent + "60" },
                      }}
                    >
                      <Box sx={{
                        width: 38, height: 38, borderRadius: "9px", flexShrink: 0,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        background: isSelected ? (isDark ? "rgba(20,184,166,.18)" : "rgba(20,184,166,.12)") : (isDark ? "rgba(255,255,255,.06)" : "#F1F5F9"),
                        border: `1px solid ${isSelected ? tokens.accent + "40" : (isDark ? "rgba(255,255,255,.08)" : "#E2E8F0")}`,
                      }}>
                        <Package size={18} color={isSelected ? tokens.accent : tokens.textSecondary} strokeWidth={1.6} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: "flex", gap: 0.6, mb: 0.5, flexWrap: "wrap" }}>
                          {tags.map((tag, ti) => (
                            <Box key={ti} sx={tagStyle(tag.color)}>
                              {tag.label}
                            </Box>
                          ))}
                          
                        </Box>
                        <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: tokens.textPrimary, lineHeight: 1.25, mb: 0.3 }}>
                          {option.description}
                        </Typography>
                        {hasSubItems && !isSelected && (
                          <Typography sx={{ fontSize: "0.72rem", color: tokens.textSecondary, lineHeight: 1.4 }}>
                            Includes: {option.productKitSubItems!.slice(0, 2).map((s) => s.productItemDescription).join(", ")}
                            {option.productKitSubItems!.length > 2 && ` +${option.productKitSubItems!.length - 2} more`}
                          </Typography>
                        )}
                      </Box>
                      <ChevronRight sx={{
                        fontSize: 18, color: isSelected ? tokens.accent : tokens.textSecondary,
                        transition: "transform .2s",
                        transform: isSelected && hasSubItems ? "rotate(90deg)" : "none",
                        flexShrink: 0,
                      }} />
                    </Box>
                    <Collapse in={isSelected && hasSubItems} timeout="auto" unmountOnExit>
                      <Box sx={{
                        px: 1.25, py: 1,
                        borderRadius: "0 0 10px 10px",
                        background: isDark ? "rgba(255,255,255,.02)" : "#F8FAFC",
                        border: `1px solid ${isSelected ? rowSelBdr : (isDark ? "rgba(255,255,255,.06)" : "#EEF2F6")}`,
                        borderTop: "none",
                      }}>
                        {option.productKitSubItems!.map((subItem, idx) => (
                          <Box key={idx} sx={{
                            display: "flex", alignItems: "center", gap: 1,
                            py: 0.75, px: 1, mb: 0.5,
                            borderRadius: "7px",
                            background: isDark ? "rgba(255,255,255,.04)" : "#fff",
                            border: `1px solid ${isDark ? "rgba(255,255,255,.06)" : "#E2E8F0"}`,
                            fontSize: "0.72rem", color: tokens.textSecondary,
                          }}>
                            <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: tokens.accent, flexShrink: 0 }} />
                            <Box sx={{ fontWeight: 600, color: tokens.textPrimary }}>{subItem.productItemHcpcsCode}</Box>
                            {subItem.size && <Box>– {subItem.size}</Box>}
                            <Box>– {subItem.productItemDescription}</Box>
                            {subItem.qty > 1 && (
                              <Box sx={{ ml: "auto", fontWeight: 700, color: tokens.accent }}>×{subItem.qty}</Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{
        ...footerSx,
        flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2,
      }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {hasSelection && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: tokens.textSecondary, whiteSpace: "nowrap" }}>
                {multiSelect ? `${selectedMultiple.length} selected` : "Selected Kit"}
              </Typography>
              {!multiSelect && selected && (
                <>
                  {getItemTags(selected).map((tag, i) => (
                    <Box key={i} sx={{ ...tagStyle(tag.color), fontSize: "0.68rem" }}>{tag.label}</Box>
                  ))}
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: tokens.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 260 }}>
                    {selected.description}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 1.25, flexShrink: 0 }}>
          <Button
            variant="contained"
            type="button"
            startIcon={<AddCircleOutlineOutlined sx={{ fontSize: "1rem !important" }} />}
            onClick={handleAdd}
            disabled={!hasSelection}
            sx={{
              ...primaryBtnSx,
              minWidth: 110, py: 0.75, px: 2,
              ...(!hasSelection && { opacity: 0.5, boxShadow: "none" }),
            }}
          >
            Select Kit{multiSelect && selectedMultiple.length > 0 ? ` (${selectedMultiple.length})` : ""}
          </Button>
           <Button
            variant="outlined"
            type="button"
            onClick={handleClose}
            sx={{ ...cancelBtnSx, minWidth: 90, py: 0.75, px: 2 }}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ProductItemSelectPopup;